import { BusinessLineEnum, BusinessLineLabels, ICommissionFormData, SourceEnum } from '@/bundles/Pipelines/types';
import { Nullable } from '@/utils/types';
import { ContactRolesEnum } from '@/bundles/Contact/enums/contactRoles';
import { IContact } from '@/bundles/Contact/interfaces';
import { contactPipelinePrimaryClientRoles } from '@/bundles/Contact/helpers/cards/contactsCard';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { formatCurrency } from '@/utils/filters';
export * from './formHelpers';
export * from './getPipelineStageLabel';
export * from './pipelineStageProbability';

const pipelinesHeadersDataBase = [
  {
    'text': 'Logo',
    'value': '',
    'sortable': false
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Company',
      'value': 'company_name',
      'sortable': true,
      'icon': 'string'
    }, {
      'text': 'Deal Name',
      'value': 'deal_name',
      'sortable': true,
      'icon': 'string'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Property Type',
      'value': 'property_type',
      'sortable': true,
      'icon': 'string'
    }, {
      'text': 'Category',
      'value': 'category',
      'sortable': true,
      'icon': 'string',
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Size',
      'value': 'size',
      'sortable': true,
      'icon': 'number'
    }, {
      'text': 'Land Size',
      'value': 'land_size',
      'sortable': true,
      'icon': 'number'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Consideration',
      'value': 'consideration',
      'sortable': true,
      'icon': 'number'
    }, {
      'text': 'Commission %',
      'value': 'commission',
      'sortable': true,
      'icon': 'number'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Probability %',
      'value': 'probability',
      'sortable': true,
      'icon': 'number'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Close Date',
      'value': 'close_date',
      'sortable': true,
      'icon': 'date'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': '% of Deal',
      'value': 'broker_percentage',
      'sortable': true,
      'icon': 'number'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [
      {
        'text': 'In House Gross',
        'value': 'in_house_gross',
        'sortable': true,
        'icon': 'number'
      },
      {
        'text': 'In House Gross Prob',
        'value': 'in_house_gross_probability',
        'sortable': true,
        'icon': 'number'
      },
      {
        'text': `${i18n.tc(LocalizationEnum.broker)} Gross`,
        'value': 'broker_gross',
        'sortable': true,
        'icon': 'number'
      },
      {
        'text': `${i18n.tc(LocalizationEnum.broker)} Gross Prob`,
        'value': 'broker_gross_probability',
        'sortable': true,
        'icon': 'number'
      }
    ]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Pipeline Stage',
      'value': 'stage',
      'sortable': true,
      'icon': 'string'
    }, {
      'text': i18n.tc(LocalizationEnum.deal_pipeline_source_category),
      'value': 'source',
      'sortable': true,
      'icon': 'string'
    }]
  },
  {
    'value': 'multi',
    'sortable': false,
    'multi': [{
      'text': 'Updated',
      'value': 'updated.date',
      'sortable': true,
      'icon': 'date'
    }, {
      'text': 'Created',
      'value': 'created.date',
      'sortable': true,
      'icon': 'date'
    }, {
      'text': 'Months Open',
      'value': 'daysOpen',
      'sortable': true,
      'icon': 'date'
    }]
  }
];

export const pipelinesHeadersData = [
  {
    'text': '',
    'sortable': false,
    'width': '1px',
    'value': 'select'
  },
  ...pipelinesHeadersDataBase
];

export const pipelinesCardHeadersData = [
  { text: '#', value: 'number', sortable: false },
  ...pipelinesHeadersDataBase
];

export const getSourceLabel = (source: SourceEnum) => {
  switch (source) {
    case SourceEnum.cold_call:
      return 'Cold Call';
    case SourceEnum.sign_call:
      return 'Sign Call';
    case SourceEnum.inbound_broker_referral:
      return `Inbound ${i18n.tc(LocalizationEnum.broker)} Referral`;
    case SourceEnum.outbound_client_referral:
      return 'Outbound Client Referral';
    case SourceEnum.inbound_web:
      return 'Inbound Web';
    case SourceEnum.past_client:
      return 'Repeat Client';
    case SourceEnum.direct_marketing:
      return 'Direct Marketing';
    case SourceEnum.client_referral:
      return 'Client Referral';
    default:
      return 'Other';
  }
};

export const getBusinessLineLabel = (businessLine: BusinessLineEnum): Nullable<string> => {
  return BusinessLineLabels[businessLine] || null;
};

export const getPipelineCommissionDisplay = (payload: ICommissionFormData): Nullable<string> => {
  if (payload.commission) {
    return `${payload.commission}%`;
  }

  const totalSquareFeet = payload.property_type?.toLowerCase() === 'land' ? payload.land_size : payload.size;
  const commissionTotal = payload.commission_total || (payload.commission_per as number) * (totalSquareFeet as number);

  if (payload.commission_per || commissionTotal) {
    return `${formatCurrency(commissionTotal)}, ${formatCurrency(payload.commission_per)}/SF`;
  }

  return null;
};

const pipelineContactBrokerRoles = [
  ContactRolesEnum.seller_broker,
  ContactRolesEnum.buyer_broker,
  ContactRolesEnum.landlord_broker,
  ContactRolesEnum.tenant_broker
];

export const getPipelineCompany = (contacts: IContact[] = []) => {
  const primaryClientCompany = contacts.find(contact => contact.roles.filter(role => contactPipelinePrimaryClientRoles.includes(role)).length)?.company || null;

  if (primaryClientCompany) {
    return primaryClientCompany;
  }

  return contacts.find(contact => contact.roles.filter(role => !pipelineContactBrokerRoles.includes(role)).length)?.company || null;
};
